<template>
  <div
    v-if="remainingFilesCount === 0"
    class="is-flex is-justify-content-center"
  >
    <span class="has-text-primary has-text-italic">
      <span class="icon"><i class="fa fa-check fa-xs" /></span>
      <span><em>All files have been assigned</em></span>
    </span>
  </div>
  <div v-else style="overflow-y: auto; max-height: 800px">
    <div v-if="showHelp" class="stage-arrow-help has-text-primary">
      <span>Stage datasets to continue</span>
      <span class="icon is-small">
        <i class="fa-solid fa-level-down-alt fa-sm" />
      </span>
    </div>
    <table :key="`builder-${id}`" class="dataloader">
      <thead>
        <tr class="">
          <th class="index"></th>
          <th class="dataset-name">
            <div>Dataset Name</div>
          </th>
          <th
            v-for="(_, dimensionIndex) of dimensionsValues"
            :key="`sumup${dimensionIndex}-header`"
            class="header-cell"
          >
            <div>Datafile {{ dimensionIndex + 1 }}</div>
          </th>
          <th class="stage frozen-right frozen-header">
            <div>
              <BaseButton
                v-tooltip="'Stage All Datasets'"
                :icon="['fa', 'fa-check-double']"
                size="small"
                :outlined="true"
                :variant="
                  stageableCandidatesIndices.length > 0 ? 'primary' : undefined
                "
                label="Stage all"
                :disabled="
                  !canStage ||
                  stageableCandidatesIndices.length === 0 ||
                  !candidateDatasetsDatafiles.length > 0
                "
                @click="stageAllDatasets()"
              />
            </div>
          </th>
        </tr>
        <tr>
          <th class="index"></th>
          <th class="dataset-name"></th>
          <th
            v-for="(dimensionValue, dimIndex) of dimensionsValues"
            :key="`constraint${dimIndex}${dimensionValue}-header`"
            class="header-cell"
          >
            <DatasetBuilderDimension
              :key="`budim${dimensionValue}${id}`"
              :files="preDimensionFilterFile"
              :dimensions-id="dimensionsId"
              :dimension-value="dimensionValue"
              :datasets-properties="datasetsProperties"
              :can-change-dimensions="canChangeDimensions"
              @select-dimension="
                (dimensionValue) => {
                  selectDimension(dimensionValue, dimIndex)
                }
              "
              @change-filter="(filt) => (dimensionsFilters[dimIndex] = filt)"
              @filter-files="
                (filtFiles) => (filteredFiles[dimIndex] = filtFiles)
              "
              @filter-matches="
                (filtMatches) => (dimensionMatches[dimIndex] = filtMatches)
              "
            ></DatasetBuilderDimension>
          </th>
          <th class="frozen-right frozen-header"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(
            {
              datasetFiles,
              datasetName,
              filesAreDifferent,
              datasetIsComplete,
              isOk,
            },
            datasetIndex
          ) of candidateDatasets"
          :key="`unlockedd-${id}${datasetFiles.length}${datasetIndex}`"
        >
          <td class="index-cell">
            <span>{{ datasetIndex + 1 }}</span>
          </td>
          <td class="dataset-name">
            <div class="dataset-name">
              <span v-tooltip="datasetName">
                {{
                  datasetName.length > 0
                    ? datasetName
                    : `Dataset ${datasetIndex + 1}`
                }}
              </span>
            </div>
          </td>
          <td
            v-for="(file, dimIndex) of datasetFiles"
            :key="`datadim${dimIndex}`"
          >
            <div class="is-flex is-align-items-center">
              <code
                v-if="file"
                class="has-text-primary"
                style="background-color: unset"
              >
                {{ file.maskedName }}
              </code>
              <span v-else><em>&lt;no file&gt;</em></span>
              <span
                v-if="file && file.maskedName !== file.value"
                v-tooltip="nameMaskTooltip"
                class="icon hoverable-icon has-text-primary"
              >
                <i class="fa fa-mask is-small" />
              </span>
            </div>
          </td>
          <td
            v-tooltip="{
              content: !filesAreDifferent
                ? 'A dataset cannot contain twice the same file'
                : !datasetIsComplete
                ? 'The dataset is not complete (missing datafile in column)'
                : 'Stage dataset',
            }"
            class="frozen-right frozen-cell"
            style="width: 1%"
          >
            <div>
              <BaseButton
                :icon="['fa', 'fa-check']"
                size="small"
                :outlined="true"
                :variant="isOk ? 'primary' : undefined"
                label="Stage"
                :disabled="!canStage || !isOk"
                @click="stageDataset(datasetIndex)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import DatasetBuilderDimension from '@/components/data-loader/DatasetBuilderDimension.vue'

import {
  commonPrefix,
  stripLeadingAndTrailingChars,
  stripLastOccurence,
  stripMask,
  trimZipExtensions,
} from '@/utils'

export default {
  name: 'DatasetBuilder',
  components: {
    DatasetBuilderDimension,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    files: {
      type: Array,
      default: () => [],
    },
    remainingFilesCount: {
      type: Number,
      default: 0,
    },
    nameMask: {
      type: String,
      default: '',
    },
    datasetsNames: {
      type: Array,
      default: () => [],
    },
    dimensionsId: {
      type: String,
      default: null,
    },
    dimensionsValues: {
      type: Array,
      default: () => [],
    },
    datasetsProperties: {
      type: Array,
      default: () => [],
    },
    canChangeDimensions: {
      type: Boolean,
      default: true,
    },
    canStage: {
      type: Boolean,
      default: false,
    },
    isUriMode: {
      type: Boolean,
      default: false,
    },
    showHelp: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filteredFiles: [],
      dimensionMatches: [],
      dimensionsFilters: [],
    }
  },
  computed: {
    dimensionsCount() {
      return this.dimensionsValues.length
    },
    nameMaskTooltip() {
      const htmlContent = `<span class='help'>Currently masking <code>${
        this.nameMask
      }</code> in file ${this.isUriMode ? 'paths' : 'names'}</span>`
      return {
        content: htmlContent,
        html: true,
      }
    },
    preDimensionFilterFile() {
      /* We somehow hit an infinite loop if we directly use this.files in
       * the template. A deep copy is needed */
      return this.files.map((f) => {
        let betterName = f.value
        betterName = trimZipExtensions(betterName)
        betterName = this.stripName(betterName)
        const maskedName = this.stripName(f.value)
        // maske
        return {
          ...f,
          name: betterName,
          maskedName,
        }
      })
    },
    filteredFilesCounts() {
      return this.filteredFiles.map((f) => f.length)
    },
    filteredFilesMaxCount() {
      return Math.max(...this.filteredFilesCounts, 0)
    },
    candidateDatasetsDatafiles() {
      const candidateDatasetsDatafiles = []
      for (
        let rowIndex = 0;
        rowIndex < this.filteredFilesMaxCount;
        rowIndex++
      ) {
        const files = this.dimensionsValues.map((_, dimensionIndex) => {
          const dimensionFiles = this.filteredFiles[dimensionIndex]
          const f = (dimensionFiles && dimensionFiles[rowIndex]) || null
          if (f) {
            // remove column match
            const dimensionMatches = this.dimensionMatches[dimensionIndex]
            const dimensionRowMatches =
              dimensionMatches && dimensionMatches[rowIndex]

            const name = dimensionRowMatches?.reduce((name, m) => {
              // iterate on all matches (strings)
              return stripLastOccurence(name, m)
            }, f.name)
            return { ...f, name: name }
          }
        })
        candidateDatasetsDatafiles.push(files)
      }
      return candidateDatasetsDatafiles
    },
    candidateDatasetsNames() {
      return this.candidateDatasetsDatafiles.map((files) => {
        const prefix = commonPrefix(
          files.map((f) => f?.name || null).filter((name) => name !== null),
        )
        return stripLeadingAndTrailingChars(prefix)
      })
    },
    candidateDatasets() {
      return this.candidateDatasetsDatafiles.map((files, rowIndex) => {
        const filesIds = files.reduce((ids, f) => {
          if (f?.id) ids.push(f?.id)
          return ids
        }, [])
        const uniqFileIds = filesIds.filter(
          (id, i) => filesIds.indexOf(id) === i, // uniqifies
        )
        const filesAreDifferent = filesIds.length === uniqFileIds.length
        const datasetIsComplete = filesIds.length === this.dimensionsCount
        return {
          datasetFiles: files,
          datasetName: this.datasetsNames[rowIndex],
          filesAreDifferent,
          datasetIsComplete,
          isOk: filesAreDifferent && datasetIsComplete,
        }
      })
    },
    stageableCandidatesIndices() {
      return this.candidateDatasets.reduce((stageableIndices, { isOk }, i) => {
        if (isOk) stageableIndices.push(i)
        return stageableIndices
      }, [])
    },
  },
  methods: {
    ...mapActions('dataloader/datasets', ['addDatasets']),
    selectDimension(dimensionValue, dimensionIndex) {
      const dimension = { index: dimensionIndex, dimensionValue }
      this.$emit('set-dimension-value', dimension)
    },
    stripName(str) {
      return stripMask(str, this.nameMask)
    },
    stageDataset(rowIndex) {
      const { datasetName: name, datasetFiles: files } =
        this.candidateDatasets[rowIndex]
      const datasets = [{ name, files }]
      this.addDatasets({ collectionId: this.id, datasets })
    },
    stageAllDatasets() {
      this.addDatasets({
        collectionId: this.id,
        datasets: this.stageableCandidatesIndices.map((i) => {
          const { datasetName: name, datasetFiles: files } =
            this.candidateDatasets[i]
          return { name, files }
        }),
      })
    },
  },
  watch: {
    dimensionsCount: {
      handler: function (newDimCount) {
        // reactivity fix, splice reactive lists
        const filtFilesLen = this.filteredFiles.length
        if (newDimCount < filtFilesLen) {
          const discardedDimsCount = filtFilesLen - newDimCount
          this.filteredFiles.splice(newDimCount, discardedDimsCount)
          this.dimensionMatches.splice(newDimCount, discardedDimsCount)
          this.dimensionsFilters.splice(newDimCount, discardedDimsCount)
        }
      },
    },
    candidateDatasetsNames: function (names) {
      this.$emit('candidate-names', names)
    },
  },
}
</script>
<style lang="scss" scoped>
td.dataset-name > .dataset-name {
  font-weight: 900;
}

.stage-arrow-help {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0.5rem;
  margin-right: 50px;

  & > .icon {
    margin: 0.5rem;
    margin-top: 1rem;
    margin-bottom: 0;
  }
}

.hoverable-icon:not(:hover) {
  opacity: 0.2;
}
</style>
